/**
 * RTWO Cryptography & Identity Engine — Public API
 * Phase 7 | Module ID: RTWO-CRYP-001
 * Version: 1.0.0-phase7
 *
 * Source: Cryptography & Identity Engine
 */

export {
  RTWOCryptographyEngine,
  getCryptographyEngine,
  KEY_LIFECYCLE,
  IDENTITY_LIFECYCLE,
} from './crypto-engine';

export type {
  RTWOKeyPair,
  RTWOAddress,
  RTWOSignature,
  RTWOIdentity,
  RTWOKeyStoragePolicy,
  RTWOTrustRecord,
  KeyPairId,
  AddressHex,
  SignatureId,
  IdentityId,
  PolicyId,
  TrustRecordId,
  PublicKeyHex,
  MessageHashHex,
  ChecksumHex,
  DerivationPath,
  CryptoTimestamp,
  CryptoAlgorithm,
  EllipticCurve,
  AddressType,
  IdentityType,
  KeyStorageType,
  TrustLevel,
  TrustSource,
  KeyStatus,
  IdentityStatus,
  SignatureStatus,
  CryptoEngineStatus,
  CryptoEvent,
  CryptoEventType,
  KeyLifecycleStage,
  IdentityLifecycleStage,
} from './domain-models';

export {
  CRYPTOGRAPHY_ENGINE_VERSION,
  CRYPTO_SERVICE_IDS,
  CRYPTO_REPOSITORY_DEFINITIONS,
  CRYPTO_CONFIGURATION_BINDINGS,
  PHASE22_CRYPTO_PROVIDER_CHECKLIST,
  KEY_LIFECYCLE as KEY_LIFECYCLE_STAGES,
  IDENTITY_LIFECYCLE as IDENTITY_LIFECYCLE_STAGES,
  KEY_STATUS_LABELS,
  KEY_STATUS_COLORS,
  IDENTITY_STATUS_LABELS,
  IDENTITY_STATUS_COLORS,
  CRYPTO_ENGINE_STATUS_LABELS,
} from './domain-models';

export type {
  ICryptoService,
  ICryptoServiceRegistry,
  ICryptoEngineOrchestrator,
  IKeyPairGenerationService,
  IAddressDerivationService,
  ISignatureCreationService,
  ISignatureVerificationService,
  IPublicKeyValidationService,
  IIdentityRegistrationService,
  IIdentityVerificationService,
  IKeyRotationService,
  IKeyStoragePolicyService,
  ITrustRecordService,
  ICryptoEventPublishingService,
  CryptoServiceStatus,
  CryptoServiceDescriptor,
  IProviderLifecycleService,
  IProviderRegistrationService,
  IConfigurationBindingService,
  IRepositoryBootstrapService,
  ConfigurationBindingResult,
  RepositoryBootstrapResult,
} from './service-interfaces';

export type {
  IKeyProvider,
  IKeyProviderRegistry,
  KeyProviderStatus,
  KeyProviderType,
  RTWOKeyProviderDescriptor,
} from './adapters/key-provider-interface';

export type {
  IIdentityBackend,
  IIdentityBackendRegistry,
  IdentityBackendStatus,
  IdentityBackendType,
  RTWOIdentityBackendDescriptor,
} from './adapters/identity-backend-interface';

export { NullKeyProvider } from './adapters/null-key-provider';
export { NullIdentityBackend } from './adapters/null-identity-backend';
export type { IHashProvider, HashAlgorithm } from './adapters/hash-provider-interface';
export { BaseKeyProvider } from './providers/base-key-provider';
export { AlgorithmKeyProvider } from './providers/algorithm-key-provider';
export { Ed25519KeyProvider } from './providers/ed25519-key-provider';
export { Secp256k1KeyProvider } from './providers/secp256k1-key-provider';
export { Sha256HashProvider } from './providers/crypto/sha256-hash-provider';
export { ScaffoldKeyProvider } from './providers/scaffold-key-provider';
export { HsmKeyProvider } from './providers/hsm-key-provider';
export { KmsKeyProvider } from './providers/kms-key-provider';
export { SecureEnclaveKeyProvider } from './providers/secure-enclave-key-provider';
export { ScaffoldIdentityBackend } from './providers/scaffold-identity-backend';
export { SystemRegistryIdentityBackend } from './providers/system-registry-identity-backend';
export { PeerNetworkIdentityBackend } from './providers/peer-network-identity-backend';
export {
  createKeyProviderCatalog,
  createIdentityBackendCatalog,
  CRYPTO_KEY_PROVIDER_IDS,
  CRYPTO_IDENTITY_BACKEND_IDS,
} from './providers/crypto-provider-catalog';
export {
  createKeyProvider,
  createOperationalKeyProviders,
  isOperationalKeyProvider,
  OPERATIONAL_KEY_PROVIDER_TYPES,
  getSha256HashProvider,
  configureSha256HashProvider,
} from './providers/crypto-provider-factory';
export {
  registerCryptoProviderCatalog,
  activateCryptoKeyProvider,
} from './bootstrap/crypto-provider-bootstrap';
export type { CryptoProviderBootstrapResult } from './bootstrap/crypto-provider-bootstrap';
export {
  keyProviderRegistry,
  identityBackendRegistry,
  KeyProviderRegistry,
  IdentityBackendRegistry,
} from './registry/provider-registries';
